import type { Space } from '@ably/spaces';
import { useSpace } from '@ably/spaces/react';
import { useContext, useEffect } from 'react';

import AppContext from '@/contexts/appContext';

const ignorePresenceError = () => undefined;

// Ably rejects a leave that lands before its enter, and a remount reuses the same space while the
// last mount's leave may still be waiting, so every enter and leave on a space waits its turn here.
const presenceQueues = new WeakMap<Space, Promise<unknown>>();

const queuePresence = (space: Space, operation: () => Promise<unknown>) => {
  const queue = (presenceQueues.get(space) ?? Promise.resolve())
    .then(operation)
    .catch(ignorePresenceError);
  presenceQueues.set(space, queue);
};

/**
 * Enters the member's encounter checklist space while an encounter is open, so
 * the backend knows which encounters are being viewed and refreshes each once,
 * however many people are looking. Must be inside that space's SpaceProvider.
 */
const useEncounterChecklistPresence = (attendanceId: string) => {
  const { space } = useSpace();
  const { currentUser } = useContext(AppContext);

  useEffect(() => {
    if (!space) return;

    queuePresence(space, () =>
      space.enter({
        attendance_id: attendanceId,
        name: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : undefined,
        avatar_url: currentUser?.avatar_url
      })
    );

    return () => queuePresence(space, () => space.leave());
  }, [attendanceId, currentUser, space]);
};

export default useEncounterChecklistPresence;
